import { useState } from "react";
import Alert from "components/Alert";
import * as utils from "common/utils";
import { HelpTooltip } from "components/Help";
import { Bar } from "react-chartjs-2";

const months = ["July", "August", "September", "October", "November", "December", "January", "February", "March", "April", "May", "June"];

const options = {
    responsive: true,
    scales: {
        x: {
            stacked: true,
        },
        y: {
            stacked: true,
            title: { display: true, text: "Percentage (%)" },
        },
    },
    plugins: {
        tooltip: {
            callbacks: {
                label: function (tooltipItem) {
                    const chart = tooltipItem.chart;
                    const dataIndex = tooltipItem.dataIndex;

                    // Sum the values from all datasets at the current index
                    const total = chart.data.datasets.reduce((sum, dataset) => {
                        const value = dataset.data[dataIndex];
                        return sum + (typeof value === "number" ? value : 0);
                    }, 0);

                    // Current value from the hovered dataset
                    const currentValue = tooltipItem.raw;

                    return `${tooltipItem.dataset.label}: ${utils.numeric.format(currentValue, 1)}% | Total: ${utils.numeric.format(total, 1)}%`;
                },
            },
        },
    },
};

export default function RedMeatPlatform({ aggregatedData, farmsWithResultsCount, farmGroup, farmLookup }) {
    const hasAggregatedData = aggregatedData && farmsWithResultsCount > 0;
    const [showGrassFed, setShowGrassFed] = useState(false);

    if (!aggregatedData.redMeatPlatform) {
        return (
            <div className="HeroPanel u-mt-md u-page-break u-print-border">
                <div className="HeroPanel-title">Red meat platform</div>
                <Alert
                    type="warning"
                    text={
                        <>
                            <p>No red meat results are present for this data set.</p>
                        </>
                    }
                />
            </div>
        );
    }

    const getData = (source) => {
        return showGrassFed
            ? months.map((m) => aggregatedData?.redMeatPlatform?.monthlyGrassFed?.find((x) => x.name === source)?.monthlyData[m]?.grassFedFeed || 0)
            : //wrap
              months.map((m) => aggregatedData?.redMeatPlatform?.monthlyGrassFed?.find((x) => x.name === source)?.monthlyData[m]?.nonGrassFedFeed || 0);
    };

    const datasets = [];
    const pastureData = getData("Pasture");
    const cropsData = getData("Crops");
    const farmSupplementsData = getData("Farm supplements");
    const importedSupplementsData = getData("Imported supplements");
    if (pastureData.reduce((a, b) => a + b, 0) > 0) {
        datasets.push({
            label: "Pasture",
            data: pastureData,
            backgroundColor: utils.GetBlockColour("ProductivePasture"),
        });
    }
    if (cropsData.reduce((a, b) => a + b, 0) > 0) {
        datasets.push({
            label: "Crops",
            data: cropsData,
            backgroundColor: utils.GetBlockColour("ProductiveCrop"),
        });
    }
    if (farmSupplementsData.reduce((a, b) => a + b, 0) > 0) {
        datasets.push({
            label: "Farm supplements",
            data: farmSupplementsData,
            backgroundColor: "rgba(153, 102, 255, 0.7)",
        });
    }
    if (importedSupplementsData.reduce((a, b) => a + b, 0) > 0) {
        datasets.push({
            label: "Imported supplements",
            data: importedSupplementsData,
            backgroundColor: "rgba(255, 99, 132, 0.7)",
        });
    }

    const data = {
        labels: months,
        datasets,
    };

    return (
        <>
            {farmGroup && !farmGroup.showAggregatedReport && farmGroup.role === "Admin" && <Alert type="warning" text="Aggregated reporting is hidden from group members" />}
            {hasAggregatedData ? (
                <>
                    <div className="HeroPanel u-mt-md u-page-break u-print-border">
                        <div className="HeroPanel-title">Red meat platform</div>

                        <Alert
                            type="info"
                            text={
                                <>
                                    <p>Shows aggregated metrics for the red meat platform </p>
                                </>
                            }
                        />

                        <div className="HeroValue u-pt-lg u-flexWrap">
                            <div className="HeroValue-item u-mb-lg">
                                <h5>Area</h5>
                                <div className="u-flex u-flexColumn">
                                    <span id="area" className="h1">
                                        {utils.numeric.format(aggregatedData.redMeatPlatform?.area)} <small>ha</small>
                                    </span>
                                </div>
                            </div>
                            <div className="HeroValue-item u-mb-lg">
                                <h5>Farms</h5>
                                <div className="u-flex u-flexColumn">
                                    <span id="farmCount" className="h1">
                                        {utils.numeric.format(aggregatedData.redMeatPlatform?.farmCount)} <small>farms</small>
                                    </span>
                                </div>
                            </div>
                            <div className="HeroValue-item u-mb-lg">
                                <h5>
                                    Wet matter from grass-fed feed
                                    <HelpTooltip tip="The estimated percentage of wet matter fed to the animals from feed that meets the New Zealand Grass-fed Administrative Standards" />
                                </h5>
                                <div className="u-flex u-flexColumn">
                                    <span id="averagePercentageOfDietFromGrassFedFeed" className="h1">
                                        {utils.numeric.format(aggregatedData.redMeatPlatform?.averagePercentageOfDietFromGrassFedFeed, 1)} <small>%</small>
                                    </span>
                                </div>
                            </div>
                            <div className="HeroValue-item u-mb-lg">
                                <h5>
                                    Grazing days
                                    <HelpTooltip tip="The estimated number of days the animals were grazing outdoors for at least 8 hours per day" />
                                </h5>
                                <div className="u-flex u-flexColumn">
                                    <span id="averageGrazingDays" className="h1">
                                        {utils.numeric.format(aggregatedData.redMeatPlatform?.averageGrazingDays)} <small>of</small>&nbsp;
                                        {utils.numeric.format(aggregatedData.redMeatPlatform?.averageDaysOnFarm)} <small>days</small>
                                    </span>
                                </div>
                            </div>
                            <div className="HeroValue-item u-mb-lg">
                                <h5>
                                    Time grazing outdoors
                                    <HelpTooltip tip="The estimated percentage of time the animals were grazing outdoors" />
                                </h5>
                                <div className="u-flex u-flexColumn">
                                    <span id="averagePercentageOfTimeWithAccessToPasture" className="h1">
                                        {utils.numeric.format(aggregatedData.redMeatPlatform?.averagePercentageOfTimeWithAccessToPasture, 1)} <small>%</small>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="u-flex u-flexWrap u-flexJustifyCenter u-mt-md">
                            <div className="ColourPanel ColourPanel--nloss">
                                <div className="u-pl-md">
                                    <h5>Nitrogen loss</h5>
                                    <div className="u-mt-sm u-pl-lg">
                                        <div className="HeroValue-item-number" id="nloss-total">
                                            {aggregatedData.redMeatPlatform?.area > 0 ? utils.numeric.format(aggregatedData.redMeatPlatform?.nitrogenLoss) : 0} <small>{"kg"}</small>
                                        </div>
                                        <div className="HeroValue-item-number" id="nloss-ha">
                                            {aggregatedData.redMeatPlatform?.area > 0 ? utils.numeric.format(aggregatedData.redMeatPlatform?.nitrogenLossPerHa, 1).toLocaleString() : 0} <small>kg/ha</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ColourPanel ColourPanel--ploss">
                                <div className="u-pl-md">
                                    <h5>Phosphorus loss</h5>
                                    <div className="u-mt-sm u-pl-lg">
                                        <div className="HeroValue-item-number" id="ploss-total">
                                            {aggregatedData.redMeatPlatform?.area > 0 ? utils.numeric.format(aggregatedData.redMeatPlatform?.phosphorusLoss, 0).toLocaleString() : 0} <small>{"kg"}</small>
                                        </div>
                                        <div className="HeroValue-item-number" id="ploss-ha">
                                            {aggregatedData.redMeatPlatform?.area > 0 ? utils.numeric.format(aggregatedData.redMeatPlatform?.phosphorusLossPerHa, 1).toLocaleString() : 0} <small>kg/ha</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ColourPanel ColourPanel--ghg">
                                <div className="u-pl-md">
                                    <h5>GHG emissions</h5>
                                    <div className="u-mt-sm u-pl-lg">
                                        <div className="HeroValue-item-number" id="ghg-total">
                                            {aggregatedData.redMeatPlatform?.area > 0 ? utils.numeric.format(aggregatedData.redMeatPlatform?.ghgEmissions) : 0} <small>t CO2e/yr</small>
                                        </div>
                                        <div className="HeroValue-item-number" id="ghg-ha">
                                            {aggregatedData.redMeatPlatform?.area > 0 ? utils.numeric.format(aggregatedData.redMeatPlatform?.ghgEmissionsPerHa) : 0} <small>kg CO2e/ha/yr</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="u-mt-xl HeroPanel">
                            <div className="u-flex u-flexJustifyBetween u-mt-md">
                                <div className="HeroPanel-heading">Wet matter percentage</div>
                                <div>
                                    <div className="Field u-mt-xs">
                                        <div className="Radio_group Radio_group--inline">
                                            <label className="Radio u-mr-md">
                                                <input checked={!showGrassFed} onChange={() => setShowGrassFed(false)} className="Radio-input" type="radio" />
                                                <span className="Radio-label">Non grass fed</span>
                                            </label>
                                            <label className="Radio">
                                                <input checked={showGrassFed} onChange={() => setShowGrassFed(true)} className="Radio-input" type="radio" />
                                                <span className="Radio-label">Grass fed</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="u-pt-lg">
                                <Bar options={options} data={data} height={100} />
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="HeroPanel">
                        <div className="Tile-body-message">
                            <h3 className="u-mt-md">There are no results for this period</h3>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
